// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useState } from "react";
import { type UseFormReturn } from "react-hook-form";

import {
  type ComparisonSelection,
  type LeaveReviewEntrypoint,
  type PostShiftReviewFormValues,
  type PostShiftReviewStep,
  QuestionTag,
  REVIEW_TYPE_TO_STEP,
  type ReviewMediaType,
  type StepAction,
  type StepActionData,
} from "../types";
import { useIsWorkplaceReviewUXUpdateEnabled } from "./useIsWorkplaceReviewUXUpdateEnabled";

interface UsePostShiftReviewNavigationProps {
  stepTypes: PostShiftReviewStep[];
  form: UseFormReturn<PostShiftReviewFormValues>;
  onSubmitRating?: (rating: number) => Promise<void>;
  onSubmitReview: (formData: PostShiftReviewFormValues) => Promise<void>;
  onSubmitReviewError: (error: unknown) => void;
  onSubmitComparison?: (selection: ComparisonSelection) => void;
  onComplete: () => void;
  closeModal: () => void;
  shiftId?: string;
  workplaceId: string;
  source: LeaveReviewEntrypoint;
}

export function usePostShiftReviewNavigation({
  stepTypes,
  form,
  onSubmitRating,
  onSubmitReview,
  onSubmitReviewError,
  onSubmitComparison,
  onComplete,
  closeModal,
  shiftId,
  workplaceId,
  source,
}: UsePostShiftReviewNavigationProps) {
  const { isEnabled: isWriteReviewUiUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmittingRatingLocally, setIsSubmittingRatingLocally] = useState(false);
  const [isSubmittingReviewLocally, setIsSubmittingReviewLocally] = useState(false);

  const currentStep: PostShiftReviewStep | undefined = stepTypes[currentIndex];
  // The last content step is always the one just before "end"
  const isLastContentStep = currentIndex === stepTypes.length - 2;

  function goNext() {
    setCurrentIndex((index) => Math.min(index + 1, stepTypes.length - 1));
  }

  function goBack() {
    // Flag OFF: never go back past pick-rating (index 0 is pick-rating)
    // Flag ON: can go back to first step (index 0)
    const minIndex = isWriteReviewUiUpdateEnabled ? 0 : 1;
    setCurrentIndex((index) => Math.max(index - 1, minIndex));
  }

  function goToEnd() {
    setCurrentIndex(stepTypes.length - 1);
  }

  function goToStep(targetStep: PostShiftReviewStep) {
    const targetIndex = stepTypes.indexOf(targetStep);
    if (targetIndex >= 0) {
      setCurrentIndex(targetIndex);
    }
  }

  function updateFormFromStepData(
    stepType: PostShiftReviewStep,
    data: StepActionData
  ): Partial<PostShiftReviewFormValues> {
    const updates: Partial<PostShiftReviewFormValues> = {};

    switch (stepType) {
      case "write-review": {
        if ("reviewText" in data) {
          form.setValue("reviewText", data.reviewText, {
            shouldValidate: true,
          });
          updates.reviewText = data.reviewText;
        }

        break;
      }

      case QuestionTag.SOFTWARE: {
        if ("answer" in data) {
          form.setValue("softwareAnswer", data.answer, {
            shouldValidate: true,
          });
          updates.softwareAnswer = data.answer;
          if ("otherText" in data) {
            form.setValue("softwareOtherText", data.otherText, {
              shouldValidate: true,
            });
            updates.softwareOtherText = data.otherText;
          }
        }

        break;
      }

      case QuestionTag.SUPPLIES: {
        if ("answer" in data) {
          form.setValue("suppliesAnswer", data.answer, {
            shouldValidate: true,
          });
          updates.suppliesAnswer = data.answer;
        }

        break;
      }

      case QuestionTag.PATIENTS_PER_NURSE: {
        if ("answer" in data) {
          form.setValue("patientCountAnswer", data.answer, {
            shouldValidate: true,
          });
          updates.patientCountAnswer = data.answer;
        }

        break;
      }

      case QuestionTag.PARKING: {
        if ("parkingAnswer" in data) {
          form.setValue("parkingAnswer", data.parkingAnswer, {
            shouldValidate: true,
          });
          updates.parkingAnswer = data.parkingAnswer;
        }

        break;
      }

      case "choose-anonymity": {
        if ("isAnonymous" in data) {
          form.setValue("isAnonymous", data.isAnonymous, {
            shouldValidate: true,
          });
          updates.isAnonymous = data.isAnonymous;
        }

        break;
      }

      case "compare-workplaces": {
        if ("comparisonSelection" in data) {
          form.setValue("comparisonSelection", data.comparisonSelection, {
            shouldValidate: true,
          });
          updates.comparisonSelection = data.comparisonSelection;
        }

        break;
      }

      case "select-review-type": {
        if ("reviewType" in data) {
          form.setValue("reviewType", data.reviewType, { shouldValidate: true });
          updates.reviewType = data.reviewType;
        }

        break;
      }

      case "video-review": {
        if ("mediaFileKey" in data) {
          form.setValue("mediaFileKey", data.mediaFileKey, { shouldValidate: true });
          form.setValue("mediaMimeType", data.mediaMimeType, { shouldValidate: true });
          form.setValue("mediaDurationSeconds", data.mediaDurationSeconds, {
            shouldValidate: true,
          });
          form.setValue("mediaFileSizeBytes", data.mediaFileSizeBytes, { shouldValidate: true });
          updates.mediaFileKey = data.mediaFileKey;
          updates.mediaMimeType = data.mediaMimeType;
          updates.mediaDurationSeconds = data.mediaDurationSeconds;
          updates.mediaFileSizeBytes = data.mediaFileSizeBytes;
        }

        break;
      }

      case "verify-employment": {
        if ("verificationDocument" in data) {
          form.setValue("verificationDocument", data.verificationDocument, {
            shouldValidate: true,
          });
          updates.verificationDocument = data.verificationDocument;
        }

        break;
      }

      default: {
        break;
      }
    }

    return updates;
  }

  function submitComparisonIfNeeded() {
    const comparisonSelection = form.getValues("comparisonSelection");
    if (comparisonSelection && onSubmitComparison) {
      onSubmitComparison(comparisonSelection);
      form.setValue("comparisonSelection", undefined);
    }
  }

  async function handleStepAction(action: StepAction) {
    if (currentStep === undefined) {
      return;
    }

    switch (action.type) {
      case "SUBMIT_RATING": {
        if (action.rating && onSubmitRating) {
          form.setValue("rating", action.rating, { shouldValidate: true });
          setIsSubmittingRatingLocally(true);
          try {
            await onSubmitRating(action.rating);
            logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
              step: "pick-rating",
              shiftId,
              source,
            });

            if (isLastContentStep) {
              setIsSubmittingReviewLocally(true);
              try {
                await onSubmitReview(form.getValues());
                submitComparisonIfNeeded();
                goToEnd();
              } catch (error: unknown) {
                onSubmitReviewError(error);
              } finally {
                setIsSubmittingReviewLocally(false);
              }
            } else {
              goNext();
            }
          } finally {
            setIsSubmittingRatingLocally(false);
          }
        }

        break;
      }

      case "NEXT_STEP": {
        // Update form data from step
        const updates = action.data ? updateFormFromStepData(currentStep, action.data) : {};

        // Check if we're finishing the last content step
        if (isLastContentStep) {
          // Submit review with current form values, defensively merging step updates
          const formData = { ...form.getValues(), ...updates };
          setIsSubmittingReviewLocally(true);
          try {
            await onSubmitReview(formData);
            submitComparisonIfNeeded();
            logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
              step: currentStep,
              shiftId,
              source,
            });
            goToEnd();

            if (currentStep === "verify-employment") {
              const hasDocument =
                action.data &&
                "verificationDocument" in action.data &&
                action.data.verificationDocument !== undefined;

              logEvent(
                hasDocument
                  ? APP_V2_USER_EVENTS.EMPLOYMENT_VERIFICATION_SUBMITTED
                  : APP_V2_USER_EVENTS.EMPLOYMENT_VERIFICATION_SKIPPED,
                { workplaceId, shiftId, source }
              );
            }
          } catch (error: unknown) {
            onSubmitReviewError(error);
          } finally {
            setIsSubmittingReviewLocally(false);
          }
        } else if (currentStep === "select-review-type" && "reviewType" in (action.data ?? {})) {
          // Jump to the selected review type step
          const { reviewType } = action.data as { reviewType: ReviewMediaType };
          logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
            step: currentStep,
            reviewType,
            shiftId,
            source,
          });
          goToStep(REVIEW_TYPE_TO_STEP[reviewType]);
        } else if (
          isWriteReviewUiUpdateEnabled &&
          (currentStep === "write-review" || currentStep === "video-review")
        ) {
          // After any review type step, jump directly to choose-anonymity
          logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
            step: currentStep,
            shiftId,
            source,
          });
          goToStep("choose-anonymity");
        } else {
          logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
            step: currentStep,
            shiftId,
            source,
          });
          goNext();
        }

        break;
      }

      case "BACK_STEP": {
        // From anonymity or any review step, go back to select-review-type
        if (
          isWriteReviewUiUpdateEnabled &&
          (currentStep === "choose-anonymity" ||
            currentStep === "write-review" ||
            currentStep === "video-review")
        ) {
          goToStep("select-review-type");
        } else {
          goBack();
        }

        break;
      }

      case "SKIP_STEP": {
        if (currentStep === "select-review-type") {
          // "I'm done for now" — submit partial review (answers only) and go to end
          // Clear review/media fields so stale content from a previous back-navigation isn't submitted
          form.setValue("reviewText", "");
          form.setValue("reviewType", undefined);
          form.setValue("mediaFileKey", undefined);
          form.setValue("mediaMimeType", undefined);
          form.setValue("mediaDurationSeconds", undefined);
          form.setValue("mediaFileSizeBytes", undefined);
          const formData = form.getValues();
          setIsSubmittingReviewLocally(true);
          try {
            await onSubmitReview(formData);
            submitComparisonIfNeeded();
            logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_QUESTION_SKIPPED, {
              step: currentStep,
              shiftId,
              source,
            });
            goToEnd();
          } catch (error: unknown) {
            onSubmitReviewError(error);
          } finally {
            setIsSubmittingReviewLocally(false);
          }
        } else if (isLastContentStep) {
          const formData = form.getValues();
          setIsSubmittingReviewLocally(true);
          try {
            await onSubmitReview(formData);
            submitComparisonIfNeeded();
            logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_QUESTION_SKIPPED, {
              step: currentStep,
              shiftId,
              source,
            });
            goToEnd();
          } catch (error: unknown) {
            onSubmitReviewError(error);
          } finally {
            setIsSubmittingReviewLocally(false);
          }
        } else {
          logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_QUESTION_SKIPPED, {
            step: currentStep,
            shiftId,
            source,
          });
          goNext();
        }

        break;
      }

      case "CLOSE_FLOW": {
        if (isWriteReviewUiUpdateEnabled) {
          submitComparisonIfNeeded();
          logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_SKIPPED, {
            step: currentStep,
            shiftId,
            source,
          });
        }

        closeModal();
        onComplete();
        break;
      }

      case "SUBMIT_REVIEW": {
        const formData = form.getValues();
        setIsSubmittingReviewLocally(true);
        try {
          await onSubmitReview(formData);
          submitComparisonIfNeeded();
          goToEnd();
        } catch (error: unknown) {
          onSubmitReviewError(error);
        } finally {
          setIsSubmittingReviewLocally(false);
        }

        break;
      }

      default: {
        // No action needed for unrecognized action types
        break;
      }
    }
  }

  return {
    // currentStep is only undefined if stepTypes is empty, which is guarded in handleStepAction.
    // Consumers always receive a valid step because usePostShiftReviewSteps always returns at least 2 entries.
    currentStep: currentStep as PostShiftReviewStep,
    currentIndex,
    handleStepAction,
    isSubmittingRatingLocally,
    isSubmittingReviewLocally,
    isFirst: currentIndex === 0,
    isLast: currentIndex === stepTypes.length - 1,
  };
}
